import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addConversationMatrixGroup,
  getConversationMatrixGroupById,
  updateConversationMatrixGroup,
} from "model/ConversationMatrixGroupModel";

const INITIAL_FORM_STATE = {
  title: "",
  status: true,
};

const conversationMatrixGroupSchema = Yup.object().shape({
  title: Yup.string().trim().required("Title is required"),
});

const parseActiveStatus = (value) =>
  value === 1 ||
  value === true ||
  value === "1" ||
  value === "active";

const ConversationMatrixGroupAddEditModal = ({
  isOpen,
  onClose,
  itemId,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    setFormKey((k) => k + 1);
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    const fieldValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!conversationMatrixGroupSchema.fields[name]) return;

    try {
      await Yup.reach(conversationMatrixGroupSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await conversationMatrixGroupSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      title: formData.title.trim(),
      status: formData.status ? "active" : "deactive",
    };

    setSubmitting(true);
    try {
      const response = itemId
        ? await updateConversationMatrixGroup(itemId, params)
        : await addConversationMatrixGroup(params);

      if (response?.success) {
        toast.success(response?.message);
        closeModal();
        onSuccess?.();
      } else {
        toast.error(response?.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!itemId) {
      resetForm();
      return;
    }

    let cancelled = false;

    const loadRecord = async () => {
      const response = await getConversationMatrixGroupById({ Id: itemId });
      if (cancelled || !response?.data) return;

      const record = response.data;
      setFormData({
        title: record?.title || "",
        status: parseActiveStatus(record?.isActive ?? record?.status),
      });
      setFormKey((k) => k + 1);
    };

    loadRecord();

    return () => {
      cancelled = true;
    };
  }, [isOpen, itemId, resetForm]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
      zIndex={5000}
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">
            {itemId ? "Edit" : "Add"} Conversation Matrix Group
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Title <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="title"
              defaultValue={formData.title}
              onChange={handleChange}
            />
            {errors.title && (
              <small className="text-danger">{errors.title}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Status</Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Choose Status</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData.status ? "Active" : "Deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    defaultChecked={formData.status}
                    onChange={handleChange}
                    className="form-check-input-padding"
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit" disabled={submitting}>
              {itemId ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ConversationMatrixGroupAddEditModal;
